import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ClipboardList, ChevronDown, Clock, FlaskConical } from "lucide-react";
import { STATE, SEVERITY, fmt, copyText } from "./shared";

function IncidentRow({ i, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(i.alert_id)} className="w-full text-left rounded-lg border p-3 hover:bg-muted/50 transition-colors" data-testid={`wt-incident-row-${i.alert_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-semibold text-sm">{i.title}</span>
        <Badge className={i.still_active ? SEVERITY[i.severity] : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"}>{i.still_active ? i.severity_word : "Resolved"}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Started {fmt(i.started_at)} · {i.still_active ? `active for ${i.duration_text}` : `resolved ${fmt(i.resolved_at)}`} · seen {i.occurrences}×
      </p>
    </button>
  );
}

export function SystemDialog({ systemId, onClose, onIncident }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [more, setMore] = useState([]);
  const load = () => api.get(`/superadmin/watchtower/systems/${systemId}`).then((r) => { setD(r.data); setMore([]); }).catch(() => setErr(true));
  useEffect(() => { if (systemId) { setD(null); setErr(false); load(); } }, [systemId]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadMore = async () => {
    const all = [...(d?.resolved_incidents || []), ...more];
    const before = all[all.length - 1]?.started_at;
    const r = await api.get(`/superadmin/watchtower/systems/${systemId}`, { params: { before } });
    setMore([...more, ...r.data.resolved_incidents]);
  };
  const copyReport = async () => { const r = await api.get(`/superadmin/watchtower/systems/${systemId}/report`); copyText(r.data.text, "System health report"); };
  const st = d ? STATE[d.state] : null;
  const resolved = [...(d?.resolved_incidents || []), ...more];
  return (
    <Dialog open={!!systemId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="wt-system-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl flex items-center gap-2 flex-wrap" data-testid="wt-system-dialog-title">
            {d ? d.name : err ? "Unable to load system" : "Loading…"}
            {d?.is_test && <Badge className="bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30"><FlaskConical className="w-3 h-3 mr-1" />{d.classification === "sandbox" ? "SANDBOX" : "TEST"}</Badge>}
            {st && <Badge className={st.badge} data-testid="wt-system-dialog-state">{d.headline}</Badge>}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1" data-testid="wt-system-dialog-freshness"><Clock className="w-3.5 h-3.5" /> {d ? d.freshness.label : ""}</DialogDescription>
        </DialogHeader>
        {d && (
          <div className="space-y-5 text-sm">
            {d.monitoring_state !== "current" && (
              <div className="rounded-lg border border-slate-400/40 bg-slate-500/10 p-3" data-testid="wt-system-stale-banner">
                <p className="font-semibold">Health data may be stale</p>
                <p className="text-muted-foreground text-xs mt-0.5">Watchtower has not received a recent health check for this system. This does not necessarily mean the client system is down.</p>
              </div>
            )}
            <Button variant="outline" className="w-full sm:w-auto" onClick={copyReport} data-testid="wt-copy-system-report"><ClipboardList className="w-4 h-4 mr-1.5" /> Copy System Health Report</Button>
            <section>
              <h4 className="font-head font-bold mb-2">Active issues <Badge variant="outline">{d.active_incidents.length}</Badge></h4>
              {d.active_incidents.length === 0 ? <p className="text-muted-foreground text-sm" data-testid="wt-no-active">No active issues.</p>
                : <div className="space-y-2" data-testid="wt-active-list">{d.active_incidents.map((i) => <IncidentRow key={i.alert_id} i={i} onOpen={onIncident} />)}</div>}
            </section>
            <section>
              <h4 className="font-head font-bold mb-2">Recently resolved</h4>
              {resolved.length === 0 ? <p className="text-muted-foreground text-sm">No resolved incidents on record.</p>
                : <div className="space-y-2" data-testid="wt-resolved-list">{resolved.map((i) => <IncidentRow key={i.alert_id} i={i} onOpen={onIncident} />)}</div>}
              {(d.resolved_has_more || more.length) && resolved.length >= 20 ? <Button variant="ghost" size="sm" className="mt-2" onClick={loadMore} data-testid="wt-load-more">Load older history</Button> : null}
            </section>
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="wt-system-tech-toggle"><ChevronDown className="w-3.5 h-3.5" /> Technical Details</CollapsibleTrigger>
              <CollapsibleContent>
                <pre className="mt-2 text-[11px] leading-relaxed whitespace-pre-wrap break-all rounded-md bg-muted p-3" data-testid="wt-system-tech-details">{[
                  `system_id: ${d.system_id}`, `tenant_id: ${d.tenant_id}`, `classification: ${d.classification}`, `enrollment: ${d.enrollment}`,
                  `polling_interval_seconds: ${d.polling_interval_seconds}`, `last_polled_at: ${d.freshness.last_polled_at ?? "—"}`, `stale_threshold_seconds: ${d.freshness.threshold_seconds}`,
                  `latest_check: ${d.latest_check ? `${d.latest_check.status} @ ${d.latest_check.checked_at} (complete=${d.latest_check.complete}, ${d.latest_check.response_time_ms} ms, check_id ${d.latest_check.check_id})` : "—"}`,
                  `reasons: ${d.latest_check?.reasons?.join(", ") || "none"}`, `checks: ${JSON.stringify(d.latest_check?.checks ?? null)}`].join("\n")}</pre>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
